'use client';

import React, { useEffect, useRef } from 'react';

import AppLogo from '@/components/ui/AppLogo';

export default function HeroSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef?.current;
    if (!el) return;
    const timer = setTimeout(() => {
      el?.querySelectorAll('.section-reveal')?.forEach((node) => {
        node?.classList?.add('visible');
      });
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section
      id="home"
      ref={sectionRef}
      className="hero-bg-pattern relative min-h-screen flex items-center overflow-hidden pt-[72px]"
    >
      {/* Abstract SVG Background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        {/* Large circle top-right */}
        <svg className="absolute -top-24 -right-24 w-[480px] h-[480px] opacity-[0.06]" viewBox="0 0 480 480" fill="none">
          <circle cx="240" cy="240" r="220" stroke="#1B3A6B" strokeWidth="2"/>
          <circle cx="240" cy="240" r="160" stroke="#1B3A6B" strokeWidth="1.5"/>
          <circle cx="240" cy="240" r="100" stroke="#2E8B57" strokeWidth="1"/>
        </svg>

        {/* Molecular structure bottom-left */}
        <svg className="absolute bottom-16 -left-16 w-[320px] h-[320px] opacity-[0.07] animate-float" viewBox="0 0 320 320" fill="none">
          <circle cx="160" cy="80" r="18" stroke="#2E8B57" strokeWidth="2"/>
          <circle cx="80" cy="200" r="18" stroke="#1B3A6B" strokeWidth="2"/>
          <circle cx="240" cy="200" r="18" stroke="#1B3A6B" strokeWidth="2"/>
          <circle cx="160" cy="260" r="12" stroke="#2E8B57" strokeWidth="1.5"/>
          <line x1="160" y1="98" x2="80" y2="182" stroke="#1B3A6B" strokeWidth="1.5"/>
          <line x1="160" y1="98" x2="240" y2="182" stroke="#1B3A6B" strokeWidth="1.5"/>
          <line x1="98" y1="200" x2="222" y2="200" stroke="#2E8B57" strokeWidth="1.5"/>
          <line x1="80" y1="218" x2="150" y2="248" stroke="#1B3A6B" strokeWidth="1"/>
          <line x1="240" y1="218" x2="170" y2="248" stroke="#1B3A6B" strokeWidth="1"/>
        </svg>

        {/* Cross/Plus medical symbol top-left */}
        <svg className="absolute top-32 left-16 w-24 h-24 opacity-[0.08] animate-float-delayed" viewBox="0 0 96 96" fill="none">
          <rect x="36" y="8" width="24" height="80" rx="4" fill="#2E8B57"/>
          <rect x="8" y="36" width="80" height="24" rx="4" fill="#1B3A6B"/>
        </svg>

        {/* DNA helix right middle */}
        <svg className="absolute right-8 top-1/3 w-[60px] h-[200px] opacity-[0.07]" viewBox="0 0 60 200" fill="none">
          <path d="M10 10 Q50 30 10 50 Q50 70 10 90 Q50 110 10 130 Q50 150 10 170 Q50 190 10 210" stroke="#1B3A6B" strokeWidth="2" fill="none"/>
          <path d="M50 10 Q10 30 50 50 Q10 70 50 90 Q10 110 50 130 Q10 150 50 170 Q10 190 50 210" stroke="#2E8B57" strokeWidth="2" fill="none"/>
        </svg>

        {/* Small dots grid */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: 'radial-gradient(circle, #1B3A6B 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 w-full">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Left: Content */}
          <div className="lg:col-span-7 space-y-8">
            {/* Badge */}
            <div className="section-reveal stagger-1 inline-flex items-center gap-2.5 px-4 py-2 bg-card border border-border rounded-full shadow-sm">
              <span className="w-2 h-2 rounded-full bg-secondary flex-shrink-0" style={{ boxShadow: '0 0 8px rgba(46,139,87,0.6)' }} />
              <span className="text-xs font-bold tracking-widest uppercase text-muted-foreground">
                Pharmaceutical Healthcare
              </span>
            </div>

            {/* H1 */}
            <h1 className="section-reveal stagger-2 font-display text-foreground leading-tight" style={{ fontSize: 'clamp(2.25rem, 5vw, 3.75rem)' }}>
              Caring for Life &{' '}
              <span className="text-secondary">Advancing</span>{' '}
              <span className="text-primary">Health</span>
            </h1>

            {/* Subtext */}
            <p className="section-reveal stagger-3 text-muted-foreground text-lg leading-relaxed max-w-xl">
              EWIN Lifescience is committed to delivering quality healthcare solutions across diverse therapeutic segments.
            </p>

            {/* CTAs */}
            <div className="section-reveal stagger-4 flex flex-wrap gap-4">
              <a href="#products" className="pharma-btn-primary">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                  <path d="M3 9H15M10 4l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Explore Products
              </a>
              <a href="#contact" className="pharma-btn-secondary">
                Get in Touch
              </a>
            </div>

            {/* Tagline strip */}
            <div className="section-reveal stagger-5 flex items-center gap-3 pt-2">
              <div className="h-px flex-1 max-w-[80px] bg-border" />
              <span className="text-xs font-semibold tracking-widest uppercase text-muted-foreground">
                Caring for Life & Advancing Health
              </span>
            </div>
          </div>

          {/* Right: Visual card */}
          <div className="lg:col-span-5 flex justify-center lg:justify-end">
            <div className="section-reveal stagger-3 relative">
              {/* Main card */}
              <div className="relative bg-card border border-border rounded-3xl p-8 shadow-xl max-w-sm w-full">
                {/* Logo large */}
                <div className="flex flex-col items-center gap-4 mb-8">
                  <div className="relative p-3 rounded-2xl bg-white shadow-lg border border-border/40">
                    <AppLogo size={120} />
                  </div>
                  <div className="text-center">
                    <div className="mt-2 text-xs text-muted-foreground tracking-widest uppercase font-medium">
                      Caring for Life & Advancing Health
                    </div>
                  </div>
                </div>

                {/* Therapeutic area pills */}
                <div className="flex flex-wrap gap-2 justify-center">
                  {[
                    'Anti-Infective', 'Respiratory', 'Gastrointestinal',
                    'Pain & Inflammation', 'Liver Care', 'Probiotic',
                    'Anti-Allergic', 'Antispasmodic',
                  ]?.map((area) => (
                    <span
                      key={area}
                      className="px-3 py-1 text-[11px] font-semibold rounded-full border border-border text-muted-foreground bg-muted"
                    >
                      {area}
                    </span>
                  ))}
                </div>

                {/* Bottom stat */}
                <div className="mt-6 pt-6 border-t border-border flex justify-around">
                  <div className="text-center">
                    <div className="text-2xl font-display text-primary">16+</div>
                    <div className="text-xs text-muted-foreground mt-0.5">Products</div>
                  </div>
                  <div className="w-px bg-border" />
                  <div className="text-center">
                    <div className="text-2xl font-display text-secondary">8</div>
                    <div className="text-xs text-muted-foreground mt-0.5">Therapeutic Areas</div>
                  </div>
                  <div className="w-px bg-border" />
                  <div className="text-center">
                    <div className="text-2xl font-display text-primary">Quality</div>
                    <div className="text-xs text-muted-foreground mt-0.5">Focused</div>
                  </div>
                </div>
              </div>

              {/* Floating pill accent */}
              <div className="absolute -top-5 -right-5 bg-secondary text-secondary-foreground px-4 py-2 rounded-full text-xs font-bold shadow-lg animate-float">
                Healthcare Solutions
              </div>
              <div className="absolute -bottom-4 -left-4 bg-primary text-primary-foreground px-4 py-2 rounded-full text-xs font-bold shadow-lg animate-float-delayed">
                Quality Focused
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-50">
        <span className="text-xs text-muted-foreground font-medium">Scroll</span>
        <div className="w-px h-10 bg-gradient-to-b from-border to-transparent" />
      </div>
    </section>
  );
}